import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CYAN = '#00F3FF';
const PURPLE = '#BC13FE';
const BG = '#030305';
const CARD_BG = '#0a0a12';
const BORDER = '#1a1a2e';

const styles = {
  page: {
    background: BG,
    minHeight: '100vh',
    color: '#e0e0e0',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    paddingTop: 100,
    paddingBottom: 80,
  },
  hero: {
    textAlign: 'center',
    padding: '60px 20px 40px',
    background: 'linear-gradient(180deg, #00080f 0%, #030305 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  heroBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}22, ${PURPLE}22)`,
    border: `1px solid ${CYAN}55`,
    borderRadius: 30,
    padding: '6px 20px',
    fontSize: 12,
    letterSpacing: 3,
    color: CYAN,
    fontWeight: 700,
    marginBottom: 20,
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 900,
    margin: '0 0 16px',
    background: `linear-gradient(135deg, #fff 0%, ${CYAN} 50%, ${PURPLE} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.15,
  },
  heroSub: {
    color: '#888',
    fontSize: '1.1rem',
    maxWidth: 640,
    margin: '0 auto 30px',
    lineHeight: 1.7,
  },
  priceBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${CYAN}33, ${PURPLE}33)`,
    border: `1px solid ${PURPLE}88`,
    borderRadius: 30,
    padding: '8px 24px',
    fontSize: 16,
    color: '#fff',
    fontWeight: 700,
  },
  glowOrb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    opacity: 0.15,
    pointerEvents: 'none',
  },
  section: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '40px 20px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: '1.35rem',
    fontWeight: 800,
    color: '#fff',
    margin: 0,
  },
  sectionTagStyle: {
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
    gap: 20,
  },
  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '22px 20px 18px',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    cursor: 'default',
    position: 'relative',
    overflow: 'hidden',
  },
  cardHover: {
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 50px ${CYAN}1a`,
    borderColor: `${CYAN}55`,
  },
  specTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 14,
  },
  specRow: {
    borderBottom: `1px solid ${BORDER}`,
  },
  specKey: {
    padding: '6px 2px',
    color: '#555',
    fontSize: 12,
    width: '44%',
  },
  specVal: {
    padding: '6px 2px',
    color: '#bbb',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'right',
  },
  priceNote: {
    fontSize: 11,
    color: '#444',
    marginBottom: 16,
  },
  btnRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  waBtn: {
    flex: 1,
    minWidth: 100,
    padding: '9px 10px',
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    border: 'none',
    borderRadius: 9,
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
  },
  gpuSpecBox: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '28px 28px 24px',
    maxWidth: 1200,
    margin: '0 auto 60px',
  },
  gpuSpecGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 14,
    marginTop: 22,
  },
  gpuSpecItem: {
    background: '#0f0f1a',
    border: `1px solid ${BORDER}`,
    borderRadius: 11,
    padding: '14px 14px',
    textAlign: 'center',
  },
  gpuSpecLabel: {
    fontSize: 11,
    color: '#555',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  gpuSpecValue: {
    fontSize: '1rem',
    fontWeight: 800,
    color: CYAN,
  },
  contactCard: {
    background: `linear-gradient(135deg, ${PURPLE}18, ${CYAN}10)`,
    border: `1px solid ${PURPLE}44`,
    borderRadius: 16,
    padding: '28px 24px',
    textAlign: 'center',
  },
};

const openWA = (planName) => {
  const text = encodeURIComponent(`Hi, I'm interested in the ${planName} plan on IndiaCloud. Please share more details.`);
  window.open(`https://wa.me/919873385555?text=${text}`, '_blank');
};

function vramBadge(label, color) {
  return (
    <div style={{
      display: 'inline-block',
      background: `${color}22`,
      border: `1px solid ${color}55`,
      borderRadius: 20,
      padding: '3px 12px',
      fontSize: 11,
      fontWeight: 700,
      color: color,
      letterSpacing: 1,
      marginBottom: 10,
      textTransform: 'uppercase',
    }}>{label}</div>
  );
}

function quoteBtnStyle(color) {
  return {
    flex: 1,
    minWidth: 100,
    padding: '9px 10px',
    background: `${color}18`,
    border: `1px solid ${color}55`,
    borderRadius: 9,
    color: color,
    fontWeight: 700,
    fontSize: 12,
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
  };
}

function sectionTag(color, label) {
  return (
    <span style={{
      background: `${color}18`,
      border: `1px solid ${color}44`,
      borderRadius: 20,
      padding: '3px 12px',
      fontSize: 11,
      color: color,
      fontWeight: 700,
      letterSpacing: 1,
      textTransform: 'uppercase',
    }}>{label}</span>
  );
}

function PlanCard({ plan, accentColor }) {
  const [hovered, setHovered] = useState(false);
  const color = accentColor || CYAN;
  return (
    <div
      style={{ ...styles.card, ...(hovered ? styles.cardHover : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100,
        background: `radial-gradient(circle at top right, ${color}18, transparent 70%)`, pointerEvents: 'none' }} />
      {vramBadge(plan.badge, color)}
      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: 14 }}>{plan.name}</div>
      <table style={styles.specTable}>
        <tbody>
          {plan.specs.map(([k, v]) => (
            <tr key={k} style={styles.specRow}>
              <td style={styles.specKey}>{k}</td>
              <td style={styles.specVal}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color, margin: '12px 0 3px' }}>{plan.price}</div>
      <div style={styles.priceNote}>{plan.note}</div>
      <div style={styles.btnRow}>
        <button style={styles.waBtn} onClick={() => openWA(plan.name)}>💬 WhatsApp</button>
        <Link to="/contact" style={quoteBtnStyle(color)}>Get Quote →</Link>
      </div>
    </div>
  );
}

const vpsPlans = [
  {
    name: 'GPU VPS Starter',
    badge: '8GB vGPU',
    price: '₹14,999/mo',
    note: '+ Tax • Blackwell vGPU',
    specs: [['vGPU', '8 GB'], ['vCPU', '8 Cores'], ['RAM', '32 GB DDR5'], ['NVMe', '300 GB'], ['Network', '1 Gbps']],
  },
  {
    name: 'GPU VPS Pro',
    badge: '16GB vGPU',
    price: '₹34,999/mo',
    note: '+ Tax • Blackwell vGPU',
    specs: [['vGPU', '16 GB'], ['vCPU', '16 Cores'], ['RAM', '64 GB DDR5'], ['NVMe', '600 GB'], ['Network', '1 Gbps']],
  },
  {
    name: 'GPU VPS Ultra',
    badge: '24GB vGPU',
    price: '₹69,999/mo',
    note: '+ Tax • Blackwell vGPU',
    specs: [['vGPU', '24 GB'], ['vCPU', '24 Cores'], ['RAM', '128 GB DDR5'], ['NVMe', '1.2 TB'], ['Network', '10 Gbps']],
  },
];

const dedicatedBlackwellPlans = [
  {
    name: 'RTX PRO 6000 Blackwell Dedicated',
    badge: '96GB GDDR7',
    price: '₹1,49,999/mo',
    note: '+ Tax • Dedicated Blackwell GPU',
    specs: [['GPU', '1× RTX PRO 6000'], ['vCPU', '32 Cores'], ['RAM', '256 GB DDR5'], ['NVMe', '3.2 TB'], ['Network', '10 Gbps']],
  },
];

const h200Plans = [
  {
    name: 'H200 AI Starter (Shared)',
    badge: 'Shared H200',
    price: '₹79,999/mo',
    note: '+ Tax • Shared H200',
    specs: [['GPU', 'Shared H200'], ['vCPU', '16 Cores'], ['RAM', '64 GB DDR5'], ['NVMe', '500 GB'], ['Network', '10 Gbps']],
  },
  {
    name: 'Dedicated H200 AI Server',
    badge: '1× H200',
    price: '₹3,99,999/mo',
    note: '+ Tax • Dedicated H200',
    specs: [['GPU', '1× NVIDIA H200'], ['vCPU', '48 Cores'], ['RAM', '512 GB DDR5'], ['NVMe', '3.2 TB'], ['Network', '25 Gbps']],
  },
];

const gpuSpecs = [
  { label: 'GPU Model', value: 'H200 SXM5' },
  { label: 'Memory', value: '141GB HBM3e' },
  { label: 'CUDA Cores', value: '16,896' },
  { label: 'FP32', value: '67 TFLOPS' },
  { label: 'FP16', value: '1,979 TFLOPS' },
  { label: 'Memory BW', value: '3.35 TB/s' },
];

export default function NvidiaH200() {
  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={{ ...styles.glowOrb, width: 500, height: 500, background: CYAN, top: -200, left: '0%' }} />
        <div style={{ ...styles.glowOrb, width: 420, height: 420, background: PURPLE, top: -160, right: '5%' }} />
        <div style={styles.heroBadge}>NVIDIA HOPPER H200</div>
        <h1 style={styles.heroTitle}>NVIDIA H200<br />AI & GPU Cloud</h1>
        <p style={styles.heroSub}>
          Next-gen HBM3e memory with 3.35 TB/s bandwidth. The ultimate GPU for frontier AI, LLM training, and scientific simulation.
        </p>
        <div style={styles.priceBadge}>Starting at ₹14,999/mo + Tax</div>
      </div>

      {/* Section A: Blackwell VPS */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Blackwell GPU VPS Series</h2>
          {sectionTag(CYAN, 'vGPU')}
        </div>
        <div style={styles.cardsGrid}>
          {vpsPlans.map((p) => <PlanCard key={p.name} plan={p} accentColor={CYAN} />)}
        </div>
      </div>

      {/* Section B: Dedicated Blackwell */}
      <div style={{ ...styles.section, paddingTop: 0 }}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Dedicated Blackwell GPU Server</h2>
          {sectionTag(PURPLE, 'Dedicated')}
        </div>
        <div style={styles.cardsGrid}>
          {dedicatedBlackwellPlans.map((p) => <PlanCard key={p.name} plan={p} accentColor={PURPLE} />)}
        </div>
      </div>

      {/* Section C: H200 AI Compute */}
      <div style={{ ...styles.section, paddingTop: 0 }}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>H200 AI Compute Series</h2>
          {sectionTag('#FF6B35', 'H200')}
        </div>
        <div style={styles.cardsGrid}>
          {h200Plans.map((p) => <PlanCard key={p.name} plan={p} accentColor='#FF6B35' />)}
        </div>
      </div>

      {/* Section D: Enterprise AI Node */}
      <div style={{ ...styles.section, paddingTop: 0 }}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Enterprise AI Node</h2>
          {sectionTag('#FFD700', 'Full Machine')}
        </div>
        <div style={styles.contactCard}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', marginBottom: 8 }}>
            🖥️ 7× RTX PRO 6000 + 1× H200 Enterprise Node
          </div>
          <div style={{ color: '#666', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
            2× AMD EPYC 9J25 &nbsp;•&nbsp; 1.5 TB DDR5 ECC &nbsp;•&nbsp; 25.6 TB U.3 NVMe &nbsp;•&nbsp; 100 Gbps Network
          </div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              style={{ ...styles.waBtn, flex: 'none', padding: '12px 28px', fontSize: 14 }}
              onClick={() => openWA('Enterprise AI Node (7× RTX PRO 6000 + 1× H200)')}
            >
              💬 WhatsApp
            </button>
            <Link
              to="/contact"
              style={{ ...quoteBtnStyle(CYAN), flex: 'none', padding: '12px 28px', fontSize: 14 }}
            >
              Contact Sales →
            </Link>
          </div>
        </div>
      </div>

      {/* GPU Spec */}
      <div style={{ padding: '0 20px' }}>
        <div style={styles.gpuSpecBox}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0 0 4px' }}>GPU Specifications</h2>
          <div style={{ color: '#555', fontSize: 12 }}>NVIDIA H200 SXM5 — Hopper Architecture</div>
          <div style={styles.gpuSpecGrid}>
            {gpuSpecs.map((s) => (
              <div key={s.label} style={styles.gpuSpecItem}>
                <div style={styles.gpuSpecLabel}>{s.label}</div>
                <div style={styles.gpuSpecValue}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
